import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';

import { TableChartConfigDto, PitcherSingleSessionStatsByPitchChartDataDto } from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import Table, { Order } from 'components/Table/Table';
import { getDisplayedColumns, tableHeaders } from 'utilities/tableHeaders';
import useReportStore from 'hooks/SessionReport/PitcherSingle/reportStore';
import PitchTypeCell from 'components/TableCells/PitchTypeCell';

export type PitcherSingleSessionStatsByPitchChartColumn = keyof PitcherSingleSessionStatsByPitchChartDataDto['statsByPitchType'][number];

const PitcherSingleSessionStatsByPitchChart = ({
  chartData,
  isLoading,
  extraChartData,
}: ChartProps<PitcherSingleSessionStatsByPitchChartDataDto, TableChartConfigDto>) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<PitcherSingleSessionStatsByPitchChartColumn | '' | 'id'>('');

  const { selectedConfgValues } = useReportStore();

  const rows = useMemo(
    () =>
      chartData?.statsByPitchType.map((row, i) => ({
        ...row,
        id: i.toString(),
      })) ?? [],
    [chartData?.statsByPitchType]
  );

  const columns = useMemo(() => {
    const displayedColumns = getDisplayedColumns<PitcherSingleSessionStatsByPitchChartColumn>(extraChartData.visibleStats);
    const pitchTypeColumnNameKey = selectedConfgValues.pitchGrouping === 'Types' ? 'pitchType' : 'parentPitchType';

    return displayedColumns.map((column) => ({
      ...tableHeaders[column],
      field: column,
      headerName: column === 'pitchType' ? t(pitchTypeColumnNameKey) : t(column),
      headerShortName: column === 'pitchType' ? t('short.' + pitchTypeColumnNameKey) : t('short.' + column),
      sortable: true,
      renderCell: column === 'pitchType' ? (cellValue) => <PitchTypeCell pitchType={cellValue} /> : undefined,
    }));
  }, [extraChartData.visibleStats, selectedConfgValues.pitchGrouping]);

  return (
    <Table
      rows={rows}
      pageState={[page, setPage]}
      orderState={[order, setOrder]}
      orderByState={[orderBy, setOrderBy]}
      columns={columns}
      loading={isLoading}
      noFooter
      tileTable
    />
  );
};

export default PitcherSingleSessionStatsByPitchChart;
